'use client';

import React, { Fragment, useEffect, useRef, useState, type JSX, type PropsWithChildren, type ReactNode } from 'react';
import { faInfo } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, IconButton, Popover } from '@mui/material';

import { california } from '@/styles/theme/colors';

function HelpInfoPopover({
  content,
  size,
  color,
  children,
  spacing = 1,
  width,
  toHtml = false,
}: {
  content: ReactNode;
  size?: 'small' | 'medium' | 'large';
  color?: string;
  spacing?: number;
  width?:
    | {
        xs?: number | string;
        sm?: number | string;
        md?: number | string;
        lg?: number | string;
        xl?: number | string;
      }
    | string
    | number;
  toHtml?: boolean;
} & PropsWithChildren): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null); // No need to pass an event here
  };

  const handleScroll = (): void => {
    // Clear the previous timeout if there is one
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }

    // Set a timeout to close the popover after 50ms
    closeTimeoutRef.current = setTimeout(() => {
      handleClose();
    }, 50); // Adjust the timeout as needed
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  let iconSize = 14;

  switch (size) {
    case 'small':
      iconSize = 12;
      break;
    case 'medium':
      iconSize = 14;
      break;
    case 'large':
      iconSize = 24;
      break;
    default:
      break;
  }

  useEffect(() => {
    if (open) {
      document.addEventListener('scroll', handleScroll, true);
    } else {
      document.removeEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('scroll', handleScroll, true);
      // Clear timeout on cleanup
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, [open]);

  return (
    <Fragment>
      <IconButton
        aria-describedby={id}
        onClick={handleClick}
        sx={{
          boxShadow: 'none !important',
          cursor: 'pointer',
          padding: 0,
          ...(!children && {
            width: iconSize,
            height: iconSize,
            svg: {
              fontSize: iconSize - 6,
            },
            border: `1px solid ${color || california[700]} `,
            borderRadius: '50% !important',
          }),
        }}
        className="helpInfoPopover"
      >
        {children ?? <FontAwesomeIcon icon={faInfo} color={color || california[700]} />}
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={(event: React.MouseEvent<HTMLButtonElement>) => {
          event.stopPropagation(); // Prevents the close event from triggering parent onClick
          handleClose();
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        sx={{
          '.MuiPaper-root': {
            maxWidth: width ?? 350,
            width,
            p: spacing,
          },
          zIndex: 2000,
        }}
        // disableScrollLock
      >
        {toHtml ? (
          <Box sx={{ typography: 'caption' }} dangerouslySetInnerHTML={{ __html: content || '' }} />
        ) : (
          <Box sx={{ typography: 'caption' }}>{content}</Box>
        )}
      </Popover>
    </Fragment>
  );
}

export default HelpInfoPopover;
